/**
===================================================
Module: frontend.src.components.SortFilterModal.tsx
Description: Modal component for sorting and filtering sessions
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SortFilterModalProps } from "../types";
import "../styles/SortFilterModal.css";

const SortFilterModal: React.FC<SortFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  onReset,
  sortOptions,
  filterOptions,
  currentSort,
  currentFilters,
  title,
}) => {
  const { t } = useTranslation('sessions');
  const [selectedSort, setSelectedSort] = useState(currentSort);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(currentFilters);

  useEffect(() => {
    if (isOpen) {
      setSelectedSort(currentSort);
      setSelectedFilters(currentFilters);
    }
  }, [isOpen, currentSort, currentFilters]);

  const handleFilterChange = (filterValue: string, checked: boolean) => {
    if (checked) {
      setSelectedFilters((prev) => [...prev, filterValue]);
    } else {
      setSelectedFilters((prev) => prev.filter((f) => f !== filterValue));
    }
  };

  const handleApply = () => {
    onApply(selectedSort, selectedFilters);
    onClose();
  };

  const handleReset = () => {
    setSelectedSort("date-newest");
    setSelectedFilters([]);
    onReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="sort-filter-overlay" onClick={onClose}>
      <div className={`sort-filter-modal ${isOpen ? 'show' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="sort-filter-header">
          <h2 className="sort-filter-title">{title}</h2>
          <button className="sort-filter-close" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="sort-filter-content">
          <div className="sort-filter-section">
            <h3 className="sort-filter-section-title">{t('sortFilter.sortBy')}</h3>
            <div className="sort-filter-options">
              {sortOptions.map((option) => (
                <label 
                  key={option.id} 
                  className={`sort-filter-option ${selectedSort === option.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="sort"
                    value={option.value}
                    checked={selectedSort === option.value}
                    onChange={(e) => setSelectedSort(e.target.value)}
                  />
                  <span className="sort-filter-option-label">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="sort-filter-section">
            <h3 className="sort-filter-section-title">{t('sortFilter.filterByStatus')}</h3>
            <div className="sort-filter-options">
              {filterOptions.map((option) => (
                <label 
                  key={option.id} 
                  className={`sort-filter-option ${selectedFilters.includes(option.value) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedFilters.includes(option.value)}
                    onChange={(e) => handleFilterChange(option.value, e.target.checked)}
                  />
                  <span className="sort-filter-option-label">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="sort-filter-actions">
          <button
            type="button"
            className="sort-filter-action secondary"
            onClick={handleReset}
          >
            {t('sortFilter.reset')}
          </button>
          <button
            type="button"
            className="sort-filter-action primary"
            onClick={handleApply}
          >
            {t('sortFilter.apply')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SortFilterModal;
